<template>
  <div class="blog">
    <Head
      :title="page.title || 'The 365 Data Science Blog'"
      :description="page.content || 'Tutorials, articles, career advice, interview tips – everything you need to know about data science.'"
    >
      <div class="subscribe-form">
        <p class="title col-11">
          Subscribe to our newsletter
        </p>
        <p class="description md-11">
          Get the latest data science tutorials, articles, and videos delivered monthly to your inbox
        </p>
        <div class="input-group">
          <input
            v-model="email" placeholder="Email Address" type="text"
            @input="inputAlert = ''"
          >
          <small v-if="inputAlert">{{ inputAlert }}</small>
          <small v-else-if="termsAgreedAlert">{{ termsAgreedAlert }}</small>
          <button
            class="btn-primary"
            @click="subscribe"
          >
            Subscribe Now
          </button>
        </div>
        <input
          id="terms"
          v-model="termsAgreed"
          type="checkbox"
          class="custom-checkbox reverse-colors"
        >
        <label for="terms" class="terms">
          I agree to 365 Data Science’s <a target="_blank" href="https://365datascience.com/terms-of-use/">Terms of Use</a> and <a target="_blank" href="https://365datascience.com/privacy-policy/">Privacy Policy</a>
        </label>
      </div>
    </Head>
    <SearchBar
      v-if="$store.state.layout === 'mobile'"
      class="container searchbar" show
      inline
    />
    <RecentPosts v-if="!$route.query.search" :results="blogInfo.recent" :categories="blogInfo.categories" />
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <OurPicks
        v-if="!$route.query.search" :results="blogInfo.featured" class="tracking-section"
        data-event="middle.scroll"
      />
    </Lazy>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <AllPosts
        :info="blogInfo" :page="page"
        :categories="blogInfo.categories"
        home
      />
    </Lazy>
    <Lazy never>
      <PreFooter
        v-if="!$route.query.search"
        title="Ready to Start Learning?"
        description="Choose your career track, build in-demand skills, and land a job in data science."
        button-title="Start Your Career"
        button-link="/signup/"
        class="tracking-section blog-pre-footer"
        data-event="end.scroll"
      />
    </Lazy>
  </div>
</template>
<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import SearchBar from '../../components/Blog/Search/SearchBar';
import Head from '../../components/Blog/Head';
import RecentPosts from '../../components/Blog/RecentPosts';
import OurPicks from '../../components/Blog/OurPicks';
import AllPosts from '../../components/Blog/AllPosts';
import PreFooter from '../../components/PreFooter';

export default {
  components: {
    Lazy,
    Head,
    SearchBar,
    RecentPosts,
    OurPicks,
    AllPosts,
    PreFooter,
  },
  mixins: [global],
  async asyncData({ $axios, route, $config }) {
    const blogInfo = await $axios.$get('/blog/index');
    const page = await $axios.$get(`/pages${route.fullPath}`);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();
    page.isBlog = true;

    return {
      blogInfo,
      email: '',
      page,
      metaTitle: `${page.metaTitle}${+route.query.page > 1 ? ` - Page ${route.query.page}` : ''}`,
    };
  },
  data() {
    return {
      termsAgreed: false,
      subscribeAccess: true,
      inputAlert: '',
      termsAgreedAlert: '',
    };
  },
  watch: {
    $route(v) {
      this.metaTitle = this.page.metaTitle;
      if (+v.query.page > 1) {
        this.metaTitle += ` - Page ${v.query.page}`;
      }
    },
    termsAgreed() {
      if (this.termsAgreed) {
        this.termsAgreedAlert = '';
      }
    },
  },
  methods: {
    async subscribe() {
      try {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!this.email || !re.test(this.email)) {
          this.inputAlert = 'Invalid email';
          return;
        }
        if (!this.termsAgreed) {
          this.termsAgreedAlert = 'Please accept the Privacy Policy';
          return;
        }

        await this.$axios.$post('/blog/subscribe', { email: this.email });
        this.$store.dispatch('dataLayer/addEvent', { event: 'BlogSubmission' });
        this.$router.push('/ty-newsletter/');
        this.email = '';
        this.termsAgreed = false;
      } catch (error) {
        console.log('error = >', error);
        console.error();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.blog {
  .blog-header {
    height: 650px;

    .subscribe-form {
      padding: var(--spacing-48);
      background: var(--main-light);
      box-shadow: 0 16px 30px rgba(0, 12, 31, 0.08);
      border-radius: 10px;

      .title {
        color: var(--main-dark-shade-0);
        font-weight: 500;
        font-size: 24px;
        line-height: 110%;
        text-align: center;
        margin: 0 auto;
      }

      .description {
        color: var(--main-dark-light-4);
        margin: var(--spacing-16) auto var(--spacing-24);
        font-weight: 400;
        font-size: 16px;
        line-height: 150%;
        text-align: center;
      }

      .input-group {
        display: block;
        margin-bottom: var(--spacing-16);
        height: unset;
        padding-right: 0;
        margin-top: 0;

        input {
          border: 1px solid rgba(0, 12, 31, 0.18);
          border-radius: 5px;
          max-width: 100%;
          width: 100% !important;
          color: rgba(0, 12, 31, 0.48);

          &::placeholder {
            color: rgba(0, 12, 31, 0.48);
          }
        }

        .btn-primary {
          width: 100%;
          margin-top: var(--spacing-12);
          padding: 18px 1.45em;
          border-radius: 5px;
        }
      }

      .terms {
        color: rgba(0, 12, 31, 0.48);
      }
    }
  }

}

* {
  box-sizing: border-box;
}

@include bp($bp-laptop) {
  .blog .blog-header .subscribe-form .title {
    font-size: 21px;
  }
}

@include bp($bp-mobile) {
  .blog {
    .blog-header {
      height: 751px;
      padding-top: 65px;
      padding-bottom: 40px;

      ::v-deep {
        h1 {
          margin-top: 37px;
          margin-bottom: 24px;
          font-weight: 500;
          font-size: 32px;
          line-height: 110%;
        }

        p {
          margin: 0;
        }
      }

      .subscribe-form {
        padding: 32px;

        .title {
          font-size: 21px;
        }

        .terms {
          margin-top: 0;
        }
      }
    }

    .searchbar {
      z-index: 2;
    }
  }
}
</style>
